/**
 * Range-aware cache for timestamped item streams.
 *
 * Common pattern: a UI fetches items in a window, then later fetches a wider window that overlaps.
 * Without help, the second fetch repeats work the client and server already paid for. This module
 * provides the building blocks (pure range arithmetic) plus a generic `RangeCache<T>` class that
 * fetches only the *gaps* between the requested window and what's already loaded.
 *
 * Caches are session-scoped (in-memory, no eviction); callers are expected to opt out of caching
 * for "live" windows (the right edge near now) by setting `liveTailMs`.
 */

export type Range = readonly [number, number];

/** Sort + coalesce a list of ranges into a normalized non-overlapping ASC list. */
export function normalizeRanges(ranges: ReadonlyArray<Range>): Range[] {
	if (ranges.length === 0) return [];
	const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
	const out: [number, number][] = [];
	for (const [s, e] of sorted) {
		if (s >= e) continue; // skip empty / inverted
		const last = out[out.length - 1];
		if (last && s <= last[1]) {
			// overlaps or touches the previous range — extend it
			if (e > last[1]) last[1] = e;
		} else {
			out.push([s, e]);
		}
	}
	return out;
}

/**
 * Subtract `loaded` (sorted, non-overlapping ASC) from `target`. Returns the 0/1/2+ gap intervals
 * that need to be fetched. `loaded` should be normalized; the caller is responsible.
 */
export function subtractRanges(target: Range, loaded: ReadonlyArray<Range>): Range[] {
	const [tStart, tEnd] = target;
	if (tStart >= tEnd) return [];
	let cursor = tStart;
	const gaps: [number, number][] = [];
	for (const [lStart, lEnd] of loaded) {
		if (lEnd <= cursor) continue; // entirely before our cursor
		if (lStart >= tEnd) break; // entirely after target
		if (lStart > cursor) {
			gaps.push([cursor, Math.min(lStart, tEnd)]);
		}
		if (lEnd > cursor) cursor = lEnd;
		if (cursor >= tEnd) break;
	}
	if (cursor < tEnd) gaps.push([cursor, tEnd]);
	return gaps;
}

/** Add `addition` to `loaded` and re-coalesce into a normalized list. */
export function mergeRanges(loaded: ReadonlyArray<Range>, addition: Range): Range[] {
	if (addition[0] >= addition[1]) return [...loaded];
	return normalizeRanges([...loaded, addition]);
}

/** True iff every point of `target` is covered by some interval in `loaded`. */
export function isFullyCovered(target: Range, loaded: ReadonlyArray<Range>): boolean {
	return subtractRanges(target, loaded).length === 0;
}

/**
 * Two-pointer merge of two sorted-ASC arrays. Stable, optionally dedupes by a key extractor.
 * Used to merge new fetch results into the cache's running item list.
 */
export function mergeSortedByTimestamp<T>(
	existing: T[],
	incoming: T[],
	timestampOf: (item: T) => number,
	keyOf?: (item: T) => string,
): T[] {
	if (existing.length === 0) return [...incoming];
	if (incoming.length === 0) return [...existing];
	const out: T[] = [];
	const seenKeys = keyOf ? new Set<string>() : null;
	let i = 0;
	let j = 0;
	while (i < existing.length && j < incoming.length) {
		const a = existing[i];
		const b = incoming[j];
		if (timestampOf(a) <= timestampOf(b)) {
			pushUnique(out, a, keyOf, seenKeys);
			i++;
		} else {
			pushUnique(out, b, keyOf, seenKeys);
			j++;
		}
	}
	while (i < existing.length) pushUnique(out, existing[i++], keyOf, seenKeys);
	while (j < incoming.length) pushUnique(out, incoming[j++], keyOf, seenKeys);
	return out;
}

function pushUnique<T>(
	out: T[],
	item: T,
	keyOf: ((item: T) => string) | undefined,
	seenKeys: Set<string> | null,
): void {
	if (keyOf && seenKeys) {
		const k = keyOf(item);
		if (seenKeys.has(k)) return;
		seenKeys.add(k);
	}
	out.push(item);
}

/** Items in `arr` (sorted ASC by `timestampOf`) whose timestamp is in `[start, end)`. Binary-search bounded. */
export function sliceByTimestamp<T>(arr: readonly T[], range: Range, timestampOf: (item: T) => number): T[] {
	const [start, end] = range;
	if (arr.length === 0 || start >= end) return [];
	// Lower bound: first index where ts >= start.
	let lo = 0;
	let hi = arr.length;
	while (lo < hi) {
		const mid = (lo + hi) >>> 1;
		if (timestampOf(arr[mid]) < start) lo = mid + 1;
		else hi = mid;
	}
	const startIdx = lo;
	// Upper bound: first index where ts >= end.
	lo = startIdx;
	hi = arr.length;
	while (lo < hi) {
		const mid = (lo + hi) >>> 1;
		if (timestampOf(arr[mid]) < end) lo = mid + 1;
		else hi = mid;
	}
	return arr.slice(startIdx, lo);
}

interface RangeCacheOpts<T> {
	/** Treat ranges whose end is within this many ms of now() as live → bypass cache. Default 60_000. */
	liveTailMs?: number;
	/** ms-epoch timestamp extractor for each item. */
	timestampOf: (item: T) => number;
	/** Optional dedupe key extractor — useful when retries can produce the same item twice. */
	keyOf?: (item: T) => string;
	/** Override now() for tests. */
	now?: () => number;
}

interface CacheEntry<T> {
	loaded: Range[]; // normalized
	items: T[]; // sorted ASC
	inflight: Map<string, Promise<T[]>>;
}

export class RangeCache<T> {
	private readonly entries = new Map<string, CacheEntry<T>>();
	private readonly liveTailMs: number;
	private readonly timestampOf: (item: T) => number;
	private readonly keyOf?: (item: T) => string;
	private readonly now: () => number;

	constructor(opts: RangeCacheOpts<T>) {
		this.liveTailMs = opts.liveTailMs ?? 60_000;
		this.timestampOf = opts.timestampOf;
		this.keyOf = opts.keyOf;
		this.now = opts.now ?? Date.now;
	}

	private getEntry(key: string): CacheEntry<T> {
		let e = this.entries.get(key);
		if (!e) {
			e = { loaded: [], items: [], inflight: new Map() };
			this.entries.set(key, e);
		}
		return e;
	}

	private isLive(range: Range): boolean {
		return range[1] > this.now() - this.liveTailMs;
	}

	/**
	 * Fetch the items for `range` for `key`. Reuses cached items + fetches only gaps.
	 * `fetchGap(key, gap)` is invoked for each missing interval (potentially in parallel).
	 */
	async query(key: string, range: Range, fetchGap: (key: string, gap: Range) => Promise<T[]>): Promise<T[]> {
		// Live-tail bypass: don't cache results whose right edge approaches now().
		if (this.isLive(range)) {
			const fresh = await fetchGap(key, range);
			return [...fresh].sort((a, b) => this.timestampOf(a) - this.timestampOf(b));
		}

		const entry = this.getEntry(key);
		const gaps = subtractRanges(range, entry.loaded);

		if (gaps.length > 0) {
			// Coalesce identical in-flight gap fetches across concurrent callers. The merge step
			// runs exactly once per gap (inside the cached promise) — concurrent callers awaiting
			// the same promise share its already-merged result.
			const gapPromises = gaps.map((gap) => {
				const k = `${gap[0]}:${gap[1]}`;
				const existing = entry.inflight.get(k);
				if (existing) return existing;
				const p = (async () => {
					const fetched = await fetchGap(key, gap);
					const incoming = [...fetched].sort((a, b) => this.timestampOf(a) - this.timestampOf(b));
					entry.items = mergeSortedByTimestamp(entry.items, incoming, this.timestampOf, this.keyOf);
					entry.loaded = mergeRanges(entry.loaded, gap);
					return fetched;
				})().finally(() => {
					entry.inflight.delete(k);
				});
				entry.inflight.set(k, p);
				return p;
			});
			await Promise.all(gapPromises);
		}

		return sliceByTimestamp(entry.items, range, this.timestampOf);
	}

	/**
	 * True iff `range` is already fully covered for `key`. Useful for callers that bypass `query`
	 * (e.g. multi-cache combined fetches) and need to decide whether to fetch.
	 */
	isCovered(key: string, range: Range): boolean {
		const entry = this.entries.get(key);
		if (!entry) return range[0] >= range[1];
		return isFullyCovered(range, entry.loaded);
	}

	/** Missing intervals within `range` for `key`. Empty array if the range is fully covered. */
	gaps(key: string, range: Range): Range[] {
		const entry = this.entries.get(key);
		return subtractRanges(range, entry?.loaded ?? []);
	}

	/** Slice the cached items for `key` to the given range. */
	slice(key: string, range: Range): T[] {
		const entry = this.entries.get(key);
		if (!entry) return [];
		return sliceByTimestamp(entry.items, range, this.timestampOf);
	}

	/**
	 * Force-feed pre-fetched data for `range` into the cache. Use when the data was obtained out-of-band
	 * (e.g. a combined fetch that populates multiple caches in one network call). Items are merged sorted;
	 * the range is marked loaded so subsequent `query` / `slice` calls find it.
	 */
	ingest(key: string, range: Range, fetched: T[]): void {
		if (range[0] >= range[1]) return;
		const entry = this.getEntry(key);
		const incoming = [...fetched].sort((a, b) => this.timestampOf(a) - this.timestampOf(b));
		entry.items = mergeSortedByTimestamp(entry.items, incoming, this.timestampOf, this.keyOf);
		entry.loaded = mergeRanges(entry.loaded, range);
	}

	/** Test/debug helper: clear all cached state. */
	clear(): void {
		this.entries.clear();
	}
}
